import React, { useEffect, useState } from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import { fmtDate, money, PAYMENT_MODES } from '../../utils/helpers';

const basisText = (e, fnName) => (e.AmountType === 'PerPerson'
  ? `${money(e.Rate)} × ${e.persons}${e.PersonsSource === 'Headcount' ? ` (${fnName(e.PersonsFunctionId) || 'headcount'})` : ''}`
  : 'Fixed');

// Desktop Expenses: totals, the list grouped by category, payments per expense.
// Logic: useExpenses.
export default function ExpensesDesktop({ vm }) {
  const {
    canView, canAdd, canEdit, canDelete, loading, groups, totals, categories, functions, fnName, headcountOf, paidByOptions,
    q, setQ, catFilter, setCatFilter, onlyDue, setOnlyDue, openService,
    form, formTotal, formPersons, openAdd, openEdit, closeForm, setF, saveExpense, deleteExpense,
    payForm, openPayAdd, openPayEdit, closePay, setPF, savePayment, deletePayment, saving,
  } = vm;

  // Payments are always listed under their expense; this hides them on demand.
  const [hidePays, setHidePays] = useState(false);
  // Right-click menu: { x, y, kind: 'expense' | 'payment', e, p? } or null.
  const [menu, setMenu] = useState(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (ev) => { if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); } };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey, true); };
  }, [menu]);
  const openMenu = (ev, kind, e, p) => {
    ev.preventDefault();
    ev.stopPropagation();
    setMenu({ x: Math.min(ev.clientX, window.innerWidth - 250), y: Math.min(ev.clientY, window.innerHeight - 170), kind, e, p });
  };
  const run = (fn) => () => { setMenu(null); fn(); };

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Expenses</h1></div>
        <div className="card card-pad empty">You do not have access to expenses. Ask an Event Admin to grant it.</div>
      </div>
    );
  }
  const cols = 8;

  return (
    <div className="expenses-page">
      <div className="page-head">
        <h1>Expenses</h1>
        <div className="spacer" />
        {canAdd && <button className="btn primary" onClick={() => openAdd()}>+ Expense</button>}
      </div>

      <div className="expense-tiles">
        <div className="expense-tile"><span>Total{totals.estimates ? ' (incl. estimates)' : ''}</span><strong>{money(totals.total)}</strong></div>
        <div className="expense-tile ok"><span>Paid</span><strong>{money(totals.paid)}</strong></div>
        <div className="expense-tile due"><span>Balance to pay</span><strong>{money(totals.balance)}</strong></div>
        <div className="expense-tile"><span>Expenses</span><strong>{totals.count}</strong><em>{totals.estimates} still estimates</em></div>
      </div>

      <div className="cards-filters">
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="expense-check"><input type="checkbox" checked={onlyDue} onChange={e => setOnlyDue(e.target.checked)} /> Only with balance due</label>
        <label className="expense-check"><input type="checkbox" checked={!hidePays} onChange={e => setHidePays(!e.target.checked)} /> Show payments</label>
        <span className="muted" style={{ fontSize: 12 }}>Right-click an expense or a payment for Edit / Delete</span>
        <input type="search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {loading ? <div className="card card-pad"><Spinner /></div> : groups.length === 0 ? (
        <div className="card card-pad empty">No expenses yet.{canAdd && ' Add the hall booking, food, clothes, decoration…'}</div>
      ) : (
        <div className="card table-wrap">
          <table className="tbl compact expense-table">
            <thead>
              <tr>
                <th className="grow">Expense</th><th>For</th><th>Basis</th>
                <th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th><th>Last paid</th><th></th>
              </tr>
            </thead>
            {groups.map(g => (
              <tbody key={g.category}>
                <tr className="expense-cat">
                  <td colSpan={3}><strong>{g.category}</strong> <span className="muted">· {g.rows.length}</span></td>
                  <td className="num"><strong>{money(g.total)}</strong></td>
                  <td className="num">{money(g.paid)}</td>
                  <td className="num">{money(g.balance)}</td>
                  <td colSpan={2} style={{ textAlign: 'right' }}>
                    {canAdd && <button className="btn light sm" onClick={() => openAdd(g.category)}>+ Add</button>}
                  </td>
                </tr>
                {g.rows.map(e => (
                  <React.Fragment key={e.ExpenseId}>
                    <tr className={'row-link' + (menu && menu.e === e && menu.kind === 'expense' ? ' open' : '')}
                      onClick={() => (e.fromService ? openService(e) : (canEdit ? openEdit(e) : null))}
                      onContextMenu={(ev) => openMenu(ev, 'expense', e)}
                      title={e.fromService ? 'Open in Services · right-click for more' : 'Edit · right-click for more'}>
                      <td className="grow">
                        <strong>{e.Title}</strong>
                        {Number(e.IsEstimate) === 1 && <span className="chip warn" style={{ marginLeft: 6 }}>estimate</span>}
                        {e.fromService && <span className="chip" style={{ marginLeft: 6 }} title="Entered on the Services page">from Services</span>}
                        {(e.VendorName || e.Notes) && <div className="sub">{[e.VendorName, e.VendorMobile, e.Notes].filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td>{e.FunctionId ? fnName(e.FunctionId) : 'Whole event'}</td>
                      <td>{basisText(e, fnName)}</td>
                      <td className="num"><strong>{money(e.total)}</strong></td>
                      <td className="num">{money(e.paid)}</td>
                      <td className="num" style={{ color: e.balance > 0 ? 'var(--danger)' : undefined }}>{money(e.balance)}</td>
                      <td>{e.LastPaymentDate ? fmtDate(e.LastPaymentDate) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={ev => ev.stopPropagation()}>
                        {canAdd && <button className="btn primary sm" onClick={() => openPayAdd(e)}>+ Pay</button>}
                        {e.fromService ? (
                          <button className="icon-btn" title="Change rate / plates / vendor on the Services page" onClick={() => openService(e)}>✎</button>
                        ) : (
                          <>
                            {canEdit && <button className="icon-btn" title="Edit" onClick={() => openEdit(e)}>✎</button>}
                            {canDelete && <button className="icon-btn danger" title="Delete" onClick={() => deleteExpense(e)}>🗑</button>}
                          </>
                        )}
                      </td>
                    </tr>
                    {!hidePays && e.payments.length > 0 && (
                      <tr className="expense-payments">
                        <td colSpan={cols}>
                          {(
                            <table className="tbl compact">
                              <thead><tr><th>Date</th><th className="num">Amount</th><th>Paid by</th><th>Mode</th><th>Notes</th><th></th></tr></thead>
                              <tbody>
                                {e.payments.map(p => (
                                  <tr key={p.ExpensePaymentId} className={menu && menu.p === p ? 'ctx-on' : undefined}
                                    onContextMenu={(ev) => openMenu(ev, 'payment', e, p)}
                                    onDoubleClick={() => canEdit && openPayEdit(p)} title="Double-click to edit · right-click to edit or delete">
                                    <td>{fmtDate(p.PaymentDate)}</td>
                                    <td className="num">{money(p.Amount)}</td>
                                    <td>{p.PaidBy || '—'}</td>
                                    <td>{p.PaymentMode || '—'}</td>
                                    <td>{p.Notes || ''}</td>
                                    <td style={{ textAlign: 'right' }}>
                                      {canEdit && <button className="icon-btn" title="Edit this payment" onClick={() => openPayEdit(p)}>✎</button>}
                                      {canDelete && <button className="icon-btn danger" title="Delete this payment" onClick={() => deletePayment(p)}>🗑</button>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {menu && (
        <div className="ctx-menu" style={{ left: menu.x, top: menu.y }} onClick={(ev) => ev.stopPropagation()} onContextMenu={(ev) => ev.preventDefault()}>
          {menu.kind === 'payment' ? (
            <>
              <div className="ctx-title">Payment {money(menu.p.Amount)} · {fmtDate(menu.p.PaymentDate)}</div>
              <button type="button" disabled={!canEdit} onClick={run(() => openPayEdit(menu.p))}>✎ Edit payment</button>
              <button type="button" className="danger" disabled={!canDelete} onClick={run(() => deletePayment(menu.p))}>🗑 Delete payment</button>
            </>
          ) : (
            <>
              <div className="ctx-title">{menu.e.Title}</div>
              <button type="button" disabled={!canAdd} onClick={run(() => openPayAdd(menu.e))}>＋ Add payment</button>
              {menu.e.fromService ? (
                <button type="button" onClick={run(() => openService(menu.e))}>✎ Open in Services (rate, plates, vendor)</button>
              ) : (
                <>
                  <button type="button" disabled={!canEdit} onClick={run(() => openEdit(menu.e))}>✎ Edit expense</button>
                  <button type="button" className="danger" disabled={!canDelete} onClick={run(() => deleteExpense(menu.e))}>🗑 Delete expense</button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <datalist id="expense-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
      <datalist id="expense-paidby">{paidByOptions.map(c => <option key={c} value={c} />)}</datalist>

      {form && (
        <Modal wide title={form.ExpenseId ? 'Edit expense' : 'New expense'} onClose={closeForm}
          footer={<><button className="btn light" onClick={closeForm} disabled={saving}>Cancel</button>
            <button className="btn primary" onClick={saveExpense} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></>}>
          <div className="form-grid cols-4 fixed-cols">
            <label className="field">Category *
              <input list="expense-categories" value={form.Category} onChange={e => setF('Category', e.target.value)} placeholder="Hall / Venue" />
            </label>
            <div className="span-2"><Field label="Expense *" value={form.Title} onChange={(v) => setF('Title', v)} placeholder="e.g. Reception hall booking" /></div>
            <Field label="For function (blank = whole event)" type="select" value={form.FunctionId}
              options={functions.map(f => ({ value: String(f.FunctionId), label: f.FunctionName }))}
              onChange={(v) => setF('FunctionId', v)} />
            <Field label="Vendor / shop" value={form.VendorName} onChange={(v) => setF('VendorName', v)} />
            <Field label="Vendor mobile" value={form.VendorMobile} onChange={(v) => setF('VendorMobile', v)} />
            <Field label="Amount is" type="select" value={form.AmountType}
              options={[{ value: 'Fixed', label: 'A fixed amount' }, { value: 'PerPerson', label: 'Rate × persons' }]}
              onChange={(v) => setF('AmountType', v)} />
            {form.AmountType === 'Fixed' ? (
              <Field label="Total amount (₹)" value={form.TotalAmount} onChange={(v) => setF('TotalAmount', v)} />
            ) : (
              <>
                <Field label="Rate per person (₹)" value={form.Rate} onChange={(v) => setF('Rate', v)} />
                <Field label="Persons from" type="select" value={form.PersonsSource}
                  options={[{ value: 'Headcount', label: 'A function’s headcount' }, { value: 'Manual', label: 'A number I type' }]}
                  onChange={(v) => setF('PersonsSource', v)} />
                {form.PersonsSource === 'Headcount' ? (
                  <Field label="Headcount of" type="select" value={form.PersonsFunctionId}
                    options={functions.map(f => ({ value: String(f.FunctionId), label: `${f.FunctionName} (${headcountOf(f.FunctionId)})` }))}
                    onChange={(v) => setF('PersonsFunctionId', v)} />
                ) : (
                  <Field label="Persons" value={form.Persons} onChange={(v) => setF('Persons', v)} />
                )}
              </>
            )}
            <div className="span-all">
              <Field label="Notes" type="textarea" rows={2} value={form.Notes} onChange={(v) => setF('Notes', v)} />
            </div>
          </div>
          <div className="expense-form-foot">
            <label className="expense-check">
              <input type="checkbox" checked={!!form.IsEstimate} onChange={e => setF('IsEstimate', e.target.checked ? 1 : 0)} />
              This is an estimate (final amount not known yet)
            </label>
            <div className="expense-form-total">
              {form.AmountType === 'PerPerson' && <span className="muted">{formPersons} persons × {money(form.Rate)} = </span>}
              <strong>{money(formTotal)}</strong>
            </div>
          </div>
        </Modal>
      )}

      {payForm && (
        <Modal title={payForm.ExpensePaymentId ? 'Edit payment' : 'Add payment'} onClose={closePay}
          footer={<><button className="btn light" onClick={closePay} disabled={saving}>Cancel</button>
            <button className="btn primary" onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></>}>
          <div className="form-grid">
            <Field label="Date *" type="date" value={payForm.PaymentDate} onChange={(v) => setPF('PaymentDate', v)} />
            <Field label="Amount (₹) *" value={payForm.Amount} onChange={(v) => setPF('Amount', v)} />
            <label className="field">Paid by
              <input list="expense-paidby" value={payForm.PaidBy} onChange={e => setPF('PaidBy', e.target.value)} />
            </label>
            <Field label="Mode" type="select" value={payForm.PaymentMode} options={PAYMENT_MODES} onChange={(v) => setPF('PaymentMode', v)} />
            <div className="span-all"><Field label="Notes" value={payForm.Notes} onChange={(v) => setPF('Notes', v)} placeholder="Receipt no., cheque no. …" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
